import { Box, SelectChangeEvent, Container } from '@mui/material';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ApiToken, ApiUser, useApiAccessService } from 'services/api-access.service';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { PageContainer, Tile, TileContent } from 'components/Layout/PageContainer';

import { TablePagination } from 'components/Pagination/TablePagination';
import { DeleteTokenConfirmationDialog } from './DeleteTokenConfirmationDialog';
import { NoTokensAvailable } from './NoTokensAvailable';
import { Button } from 'components/Button';

interface UserTokensTableProps {
  userId: string;
  userName?: string;
}

export function UserTokensTable({ userId, userName = undefined }: UserTokensTableProps) {
  const intl = useIntl();
  const { getTokensForUser, revokeToken, revokeAllTokens } = useApiAccessService();

  const [user, setUser] = useState<ApiUser | null>(null);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setModalOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<ApiToken | null>(null);
  const [isDeleteAll, setDeleteAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUserTokens = useCallback(async () => {
    try {
      const { data, error } = await getTokensForUser(userId);
      if (error) {
        setLoadError(error.message);
        setTokens([]);
        setUser(null);
        return;
      }

      if (data) {
        setTokens(data.tokens);
        setUser(data.user);
      } else {
        setTokens([]);
        setUser(null);
      }
    } catch (e) {
      console.error('Failed to load tokens.', e);
      setLoadError('Failed to load user tokens.');
      setTokens([]);
      setUser(null);
    } finally {
      setInitialLoading(false);
    }
  }, [getTokensForUser, userId]);

  useEffect(() => {
    fetchUserTokens();
  }, [fetchUserTokens]);

  const handleRevoke = async (tokenId: string) => {
    await revokeToken(tokenId);
    await fetchUserTokens();
  };

  const handleRevokeAll = async () => {
    await revokeAllTokens(userId);
    await fetchUserTokens();
  };

  const openDeleteOneDialog = (token: ApiToken) => {
    setTokenToDelete(token);
    setModalOpen(true);
    setDeleteAll(false);
  };

  const openDeleteAllDialog = () => {
    setDeleteAll(true);
    setTokenToDelete(null);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    try {
      if (isDeleteAll) {
        await handleRevokeAll();
      } else if (tokenToDelete) {
        await handleRevoke(tokenToDelete.id);
      }
    } catch (err) {
      console.error('Failed to revoke token(s)', err);
    } finally {
      setModalOpen(false);
      setIsDeleting(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(event.target.value as number);
    setPage(0);
  };

  const columns: ColumnDef<ApiToken>[] = [
    {
      id: 'id',
      accessorKey: 'id',
      header: intl.formatMessage({
        id: 'api_access.user_tokens.table.refresh_token_id',
      }),
    },
    {
      id: 'generatedOn',
      header: intl.formatMessage({
        id: 'api_access.user_tokens.table.generated_on',
      }),
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'expiresOn',
      header: intl.formatMessage({
        id: 'api_access.user_tokens.table.expires_on',
      }),
      cell: ({ row }) => formatDate(row.original.expiresAt),
    },
    {
      id: 'action',
      header: intl.formatMessage({ id: 'api_access.user_tokens.table.action' }),
      // The table API requires a per-row callback to retain the current token.
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Button icon="Trash" aria-label="delete token" onClick={() => openDeleteOneDialog(row.original)} />
        </div>
      ),
    },
  ];

  const paginatedTokens = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return tokens.slice(start, end);
  }, [tokens, page, rowsPerPage]);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid Date';
    return format(date, 'dd/MM/yyyy HH:mm');
  };

  const table = useReactTable({
    data: paginatedTokens,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalCount = tokens.length;

  const userNameTitle = (
    <FormattedMessage
      id="api_access.user_tokens.user_name"
      defaultMessage="for {name}"
      values={{ name: userName ?? user?.name ?? '...' }}
    />
  );

  if (initialLoading) {
    return (
      <Container className="px-10">
        <PageContainer>
          <Tile titleId="api_access.user_tokens.title" additionalTitle={userNameTitle}>
            <TileContent>
              <Box className="flex justify-center items-center py-8">
                <LoadingSpinner />
              </Box>
            </TileContent>
          </Tile>
        </PageContainer>
      </Container>
    );
  }

  if (tokens.length === 0) {
    if (loadError) {
      return (
        <Container className="px-10">
          <PageContainer>
            <Tile
              titleId="api_access.user_tokens.title"
              additionalTitle={userNameTitle}
              errorDetails={loadError}
              testId="api-user-tokens-error"
              rightHandTitleComponent={
                <Button priority="secondary" className="whitespace-nowrap" disabled>
                  <FormattedMessage id="api_access.user_tokens.delete_all_button" />
                </Button>
              }
            />
          </PageContainer>
        </Container>
      );
    }

    return (
      <Container className="px-10">
        <PageContainer>
          <Tile titleId="api_access.user_tokens.title" additionalTitle={userNameTitle}>
            <TileContent>
              <NoTokensAvailable />
            </TileContent>
          </Tile>
        </PageContainer>
      </Container>
    );
  }

  return (
    <Container className="px-10">
      <PageContainer>
        <Tile
          titleId="api_access.user_tokens.title"
          additionalTitle={userNameTitle}
          rightHandTitleComponent={
            <Button
              priority="secondary"
              onClick={openDeleteAllDialog}
              className="whitespace-nowrap"
              disabled={Boolean(loadError)}
            >
              <FormattedMessage id="api_access.user_tokens.delete_all_button" defaultMessage="Delete all Tokens" />
            </Button>
          }
        >
          <TileContent>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full table-fixed">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="h-[48px] border-b">
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="border-r px-2 text-left text-sm font-bold last:border-r-0"
                          style={{
                            minWidth: { id: 200, generatedOn: 150, expiresOn: 120, action: 20 }[header.column.id],
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="h-[48px] border-b last:border-b-0">
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className="break-words border-r px-2 text-sm text-text-primary last:border-r-0"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageLabelId="api_access.user_tokens.rows_per_page"
            />
          </TileContent>
        </Tile>
      </PageContainer>
      <DeleteTokenConfirmationDialog
        open={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setIsDeleting(false);
        }}
        onConfirm={() => {
          handleConfirmDelete().catch(err => {
            console.error('Failed to handle confirm delete', err);
          });
        }}
        token={tokenToDelete ?? undefined}
        deleteAll={isDeleteAll}
        userName={user?.name}
        loading={isDeleting}
      />
    </Container>
  );
}
